import { checkSvg, inspectRaster } from '@fin/shared';
import { IconPreview, iconTask } from './IconPreview.tsx';
import { useEffect, useRef, useState } from 'react';
import type { IconAttribution, IconSearchResponse, IconSearchResult } from '@fin/shared';
import { get, post, qs } from '../lib/api.ts';
import { Button, Input, Segmented, Select, Tile, cx } from './ui.tsx';

type Source = 'all' | 'iconify' | 'wikimedia';
export function IconImagePicker({ value, attribution, color, name, onChange, onBusyChange }: {
  value: string | null; attribution: IconAttribution | null; color: string; name: string;
  onChange: (image: string | null, attribution: IconAttribution | null) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const alive = useRef(true);
  const sequence = useRef(0);
  const importing = useRef(false);
  const [mode, setMode] = useState<'file' | 'url' | 'search'>('file');
  const [over, setOver] = useState<'ok' | 'bad' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [query, setQuery] = useState(name);
  const [source, setSource] = useState<Source>('all');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [results, setResults] = useState<IconSearchResult[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [searched, setSearched] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [actualQuery, setActualQuery] = useState('');
  const [lastSearch, setLastSearch] = useState<{ query: string; source: Source; color: string } | null>(null);
  const [nextOffset, setNextOffset] = useState(0);
  useEffect(() => { alive.current = true; return () => { alive.current = false; sequence.current++; }; }, []);
  useEffect(() => { onBusyChange(busy); }, [busy, onBusyChange]);
  useEffect(() => () => onBusyChange(false), [onBusyChange]);

  const changeMode = (next: typeof mode) => { setMode(next); setError(null); };
  const take = async (file: Blob | undefined, credits: IconAttribution | null = null, fit = false) => {
    setOver(null);
    if (!file || importing.current) return;
    if (!file.type.startsWith('image/')) { setError('Это не картинка — нужен PNG, JPEG, WebP или SVG'); return; }
    if (file.size > 15 * 1024 * 1024) { setError('Файл больше 15 МБ'); return; }
    importing.current = true; setBusy(true); setError(null);
    try {
      const picture = await imageThumb(file, fit);
      if (alive.current) onChange(picture, credits);
    } catch (e) {
      if (alive.current) setError((e as Error).message);
    } finally { importing.current = false; if (alive.current) setBusy(false); }
  };
  const importUrl = async (value: string, credits?: IconAttribution) => {
    if (importing.current) return;
    let parsed: URL;
    try { parsed = new URL(value.trim()); if (parsed.protocol !== 'https:') throw new Error(); }
    catch { setError('Укажите прямую HTTPS-ссылку на картинку'); return; }
    importing.current = true; setBusy(true); setError(null);
    try {
      const data = await iconTask(() => post<{ dataUrl: string }>('/icons/import', { url: parsed.href }));
      const blob = await (await fetch(data.dataUrl)).blob();
      const picture = await imageThumb(blob, true);
      if (alive.current) onChange(picture, credits ?? { source: 'url', url: parsed.origin + parsed.pathname, author: '', license: '' });
    } catch (e) {
      if (alive.current) setError((e as Error).message);
    } finally { importing.current = false; if (alive.current) setBusy(false); }
  };
  const resetSearch = () => {
    sequence.current++; setSearching(false); setResults([]); setSearched(false); setHasMore(false);
    setSearchError(null); setWarnings([]); setActualQuery(''); setLastSearch(null); setNextOffset(0);
  };
  const search = async (more = false) => {
    const request = more && lastSearch ? lastSearch : { query: query.trim(), source, color };
    if (!request.query) return;
    const version = ++sequence.current;
    setSearching(true); setSearchError(null);
    if (!more) { setResults([]); setSearched(false); setHasMore(false); setWarnings([]); setActualQuery(''); }
    try {
      const data = await get<IconSearchResponse>('/icons/search' + qs({ q: request.query, source: request.source,
        offset: more ? nextOffset : 0, color: request.color }));
      if (!alive.current || version !== sequence.current) return;
      setResults((prev) => more ? [...prev, ...data.items.filter((item) => !prev.some((shown) => shown.id === item.id))] : data.items);
      setNextOffset((more ? nextOffset : 0) + data.items.length);
      setHasMore(data.hasMore); setWarnings(data.warnings); setActualQuery(data.query); setLastSearch(request); setSearched(true);
    } catch (e) {
      if (alive.current && version === sequence.current) setSearchError((e as Error).message);
    } finally { if (alive.current && version === sequence.current) setSearching(false); }
  };
  return (
    <div className="icon-picker" aria-label="Картинка категории">
      <div className={cx('icon-drop', over === 'ok' && 'over', over === 'bad' && 'reject')} aria-busy={busy}
        onDragOver={(e) => { e.preventDefault(); const type = e.dataTransfer.items[0]?.type ?? ''; setOver(type.startsWith('image/') ? 'ok' : 'bad'); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(null); }}
        onDrop={(e) => { e.preventDefault(); void take(e.dataTransfer.files[0]); }}
        onPaste={(e) => { const file = [...e.clipboardData.files].find((f) => f.type.startsWith('image/')); if (file) { e.preventDefault(); void take(file); } }}>
        <Tile icon="folder" color={color} image={value} size="lg" />
        <div className="icon-drop-text">
          <strong>{value ? 'Своя картинка' : 'Своя картинка вместо иконки'}</strong>
          <span className="muted small" role="status">{busy ? 'Загружаю…' : over === 'bad' ? 'Сюда можно только картинку' : over === 'ok' ? 'Отпустите, чтобы поставить' : 'Перетащите картинку или выберите способ ниже'}</span>
        </div>
        {value ? <div className="icon-drop-actions"><Button size="sm" variant="ghost" disabled={busy} onClick={() => { onChange(null, null); setError(null); }}>Убрать</Button></div> : null}
      </div>
      <Segmented value={mode} onChange={changeMode} label="Способ выбора картинки" wide options={[
        { value: 'file', label: 'Файл' }, { value: 'url', label: 'Ссылка' }, { value: 'search', label: 'Поиск' },
      ]} />
      {mode === 'file' ? <div>
        <Button size="sm" onClick={() => input.current?.click()} busy={busy}>{value ? 'Заменить файл' : 'Выбрать файл'}</Button>
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" hidden
          onChange={(e) => { void take(e.target.files?.[0], null, e.target.files?.[0]?.type === 'image/svg+xml'); e.target.value = ''; }} />
      </div> : null}
      {mode === 'url' ? <form className="icon-picker-form" onSubmit={(e) => { e.preventDefault(); void importUrl(url); }}>
        <Input type="url" value={url} placeholder="https://…/image.png" aria-label="Ссылка на картинку" maxLength={2048}
          autoCapitalize="none" autoCorrect="off" onChange={(e) => setUrl(e.target.value)} />
        <Button type="submit" size="sm" busy={busy} disabled={!url.trim()}>Загрузить</Button>
      </form> : null}
      {mode === 'search' ? <div className="icon-picker-search">
        <form className="icon-picker-form" onSubmit={(e) => { e.preventDefault(); void search(); }}>
          <Input value={query} placeholder="Название, например Spotify" aria-label="Найти иконку" maxLength={100}
            onChange={(e) => { resetSearch(); setQuery(e.target.value); }} />
          <Select value={source} aria-label="Источник иконок" onChange={(e) => { resetSearch(); setSource(e.target.value as Source); }}>
            <option value="all">Все источники</option><option value="iconify">Iconify</option><option value="wikimedia">Wikimedia</option>
          </Select>
          <Button type="submit" size="sm" busy={searching} disabled={!query.trim()}>Найти</Button>
        </form>
        {actualQuery && actualQuery !== query.trim().toLowerCase() ? <p className="muted small">Поиск: {actualQuery}</p> : null}
        {warnings.map((warning) => <p key={warning} className="muted small" role="status">{warning}</p>)}
        {searchError ? <p className="neg small" role="alert">{searchError}</p> : null}
        <p className="muted small">Поисковый запрос отправляется выбранным сервисам. Превью загружаются через сервер.</p>
        <div className="icon-search-results" aria-label="Найденные иконки" aria-busy={searching}>
          {results.map((item) => <div key={item.id} className="icon-search-result">
            <button type="button" className={cx('icon-search-choice', attribution?.url === item.attribution.url && 'on')}
              disabled={busy} aria-pressed={attribution?.url === item.attribution.url} aria-label={`Выбрать ${item.title}`}
              onClick={() => void importUrl(item.imageUrl, item.attribution)}>
              <IconPreview url={item.imageUrl} />
              <span>{item.title}</span>
            </button>
            <a className="small" href={item.attribution.url} target="_blank" rel="noreferrer">{item.attribution.source === 'iconify' ? 'Iconify' : 'Wikimedia'}</a>
            <span className="muted small">{item.attribution.license}</span>
          </div>)}
        </div>
        {searched && !results.length ? <p className="muted small" role="status">Ничего не найдено. Попробуйте другое название или другой источник.</p> : null}
        {hasMore ? <Button size="sm" busy={searching} onClick={() => void search(true)}>Показать ещё</Button> : null}
      </div> : null}
      {error ? <p className="neg small" role="alert">{error}</p> : null}
      {value && attribution ? <div className="icon-credits small">
        <a href={attribution.url} target="_blank" rel="noreferrer">Источник картинки</a>
        {attribution.author ? <span>{attribution.author}</span> : null}
        {attribution.license ? attribution.licenseUrl
          ? <a href={attribution.licenseUrl} target="_blank" rel="noreferrer">{attribution.license}</a>
          : <span>{attribution.license}</span> : null}
      </div> : null}
    </div>
  );
}

/** Rasterise through an isolated image context; SVG markup is never inserted in the document. */
export async function imageThumb(file: Blob, fit = false): Promise<string> {
  if (file.size > 15 * 1024 * 1024) throw new Error('Картинка слишком большая');
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (file.type === 'image/svg+xml') checkSvg(new TextDecoder().decode(bytes));
  else if (inspectRaster(bytes).type !== file.type) throw new Error('Содержимое картинки не соответствует формату');
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => { image.src = ''; reject(new Error('Не удалось прочитать картинку вовремя')); }, 10_000);
      image.onload = () => { clearTimeout(timer); resolve(); };
      image.onerror = () => { clearTimeout(timer); reject(new Error('Не удалось прочитать картинку. Попробуйте PNG, JPEG или SVG')); };
      image.src = objectUrl;
    });
    const width = image.naturalWidth, height = image.naturalHeight;
    if (!width || !height || width * height > 16_000_000) throw new Error('Картинка слишком большая. Выберите изображение до 16 мегапикселей');
    const canvas = document.createElement('canvas');
    canvas.width = 96; canvas.height = 96;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Не удалось обработать картинку');
    ctx.imageSmoothingQuality = 'high';
    if (fit) {
      // An inset keeps a square logo's corners inside the circular category tile.
      const scale = 68 / Math.max(width, height);
      ctx.drawImage(image, (96 - width * scale) / 2, (96 - height * scale) / 2, width * scale, height * scale);
    } else {
      const side = Math.min(width, height);
      ctx.drawImage(image, (width - side) / 2, (height - side) / 2, side, side, 0, 0, 96, 96);
    }
    const webp = canvas.toDataURL('image/webp', 0.85);
    return webp.startsWith('data:image/webp') ? webp : canvas.toDataURL('image/png');
  } finally { URL.revokeObjectURL(objectUrl); }
}
